import type { LensCard, LensDataset } from '~/types/leLens';

import type { LensTurnEffort } from './buildSession';
import type { RowProbe } from './checkSql';
import { columnsTheViewReads, sqlCouldReturn } from './datasetView';
import type { BuildPool } from './lensAskSession';
import { emptyResultPrompt } from './prompts';

export type EmptyQuery = { id: string; title: string };

export type EmptyQueryRepair = {
  datasets: Array<LensDataset>;
  /** Still returning nothing after the attempt, so the reader is told rather than shown blank cards. */
  unanswered: Array<EmptyQuery>;
};

/**
 * Rewrites the finished board's queries that answer nothing.
 *
 * Run last, once, because emptiness is the one fault that cannot be found
 * while writing: a query is only observably empty after it runs, and running
 * every candidate as it is written would bill a read per attempt. Run as a
 * single turn over every empty query rather than one turn each, because the
 * cause is usually one vocabulary mistake repeated — the model that wrote
 * `category = 'Hotel'` for one card wrote it for all of them, and looking the
 * value up once fixes the set.
 *
 * A rewrite is only kept once it has been seen to return rows, and only while
 * every card bound to it still reads columns it returns.
 */
export async function repairEmptyQueries({
  cards,
  datasets,
  authored,
  pool,
  probe,
  effort,
  onStatus,
  abandoned,
}: {
  cards: ReadonlyArray<LensCard>;
  datasets: ReadonlyArray<LensDataset>;
  /** Ids this build wrote. Omitted checks the whole board; a card saved earlier is not re-litigated. */
  authored?: ReadonlyArray<string>;
  pool: BuildPool;
  probe: RowProbe;
  effort?: LensTurnEffort;
  onStatus: (message: string | null) => void;
  abandoned?: () => boolean;
}): Promise<EmptyQueryRepair> {
  const unchanged = { datasets: [...datasets], unanswered: [] };
  const queries = probeableQueries(datasets, authored);
  if (!queries.length || abandoned?.()) {
    return unchanged;
  }

  onStatus(queries.length === 1 ? 'Checking the query returns rows…' : 'Checking every query returns rows…');
  const empty = (await probe(queries.map(({ id, sql }) => ({ id, sql })))).filter((verdict) => verdict.empty);
  if (!empty.length || abandoned?.()) {
    return unchanged;
  }

  const failing = queries.filter((query) => empty.some((verdict) => verdict.id === query.id));
  onStatus(
    failing.length === 1
      ? `“${failing[0].title}” returns no rows. Finding out why…`
      : `${failing.length} queries return no rows. Finding out why…`,
  );

  const answered = await pool.primary.ask(emptyResultPrompt(failing), {
    onProgress: onStatus,
    ...(effort ? { effort } : {}),
  });
  if (abandoned?.()) {
    return unchanged;
  }

  const corrections = correctionsByQueryId(answered.sqlBlocks, failing);
  const holding = corrections.filter((correction) => keepsBoundCardsReadable(correction, cards, datasets));
  if (!holding.length) {
    return { ...unchanged, unanswered: failing.map(asAsked) };
  }

  onStatus(holding.length === 1 ? 'Checking the correction returns rows…' : 'Checking the corrections return rows…');
  const reprobed = await probe(holding.map(({ id, sql }) => ({ id, sql })));
  const accepted = holding.filter((correction) =>
    reprobed.some((verdict) => verdict.id === correction.id && !verdict.empty && !verdict.error),
  );

  return {
    datasets: datasets.map((dataset) => applyToDataset(dataset, accepted)),
    unanswered: failing.filter((query) => !accepted.some((correction) => correction.id === query.id)).map(asAsked),
  };
}

type ProbeableQuery = { id: string; title: string; sql: string };

function probeableQueries(
  datasets: ReadonlyArray<LensDataset>,
  authored: ReadonlyArray<string> | undefined,
): Array<ProbeableQuery> {
  const wrote = (id: string) => !authored || authored.includes(id);
  return datasets
    .filter((dataset) => dataset.sql.trim() && wrote(dataset.id))
    .map((dataset) => ({ id: dataset.id, title: dataset.name, sql: dataset.sql }));
}

function asAsked(query: ProbeableQuery): EmptyQuery {
  return { id: query.id, title: query.title };
}

const QUERY_MARKER = /^\s*--\s*query:\s*(\S+)[^\n]*\n?/i;

/**
 * The corrected query for each id the answer named.
 *
 * Matched by the marker line the prompt asks for rather than by position: the
 * turn also runs its own lookups, so the blocks it comes back with are not one
 * per failing query and not in the order they were asked about. A block naming
 * nothing, or naming a query that was not failing, is not a correction.
 */
function correctionsByQueryId(
  blocks: ReadonlyArray<string>,
  failing: ReadonlyArray<ProbeableQuery>,
): Array<ProbeableQuery> {
  const corrected = new Map<string, string>();
  for (const block of blocks) {
    const marked = QUERY_MARKER.exec(block);
    if (!marked) {
      continue;
    }
    const named = failing.find((query) => query.id === marked[1]);
    const sql = block.slice(marked[0].length).trim();
    if (named && sql) {
      corrected.set(named.id, sql);
    }
  }
  return failing
    .filter((query) => corrected.has(query.id))
    .map((query) => ({ ...query, sql: corrected.get(query.id) }));
}

/** The guard a concurrent build already applies: a rewritten dataset may not orphan a card's view. */
function keepsBoundCardsReadable(
  correction: ProbeableQuery,
  cards: ReadonlyArray<LensCard>,
  datasets: ReadonlyArray<LensDataset>,
): boolean {
  const existing = datasets.find((dataset) => dataset.id === correction.id);
  if (!existing) {
    return false;
  }
  return cards.every(
    (card) => card.datasetId !== correction.id || sqlCouldReturn(correction.sql, columnsTheViewReads(card.view)),
  );
}

function applyToDataset(dataset: LensDataset, accepted: ReadonlyArray<ProbeableQuery>): LensDataset {
  const correction = accepted.find(({ id }) => id === dataset.id);
  return correction ? { ...dataset, sql: correction.sql } : dataset;
}

/** Said at the save, because a query that saved and answers nothing is not a card that failed. */
export function withEmptyNote(message: string, unanswered: ReadonlyArray<EmptyQuery>): string {
  if (!unanswered.length) {
    return message;
  }
  const still = unanswered.length === 1 ? `“${unanswered[0].title}” returns` : `${unanswered.length} queries return`;
  return `${message} — ${still} no rows for this period`;
}
